import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useMideyeAuth, type MideyeAuthContextValue } from "@mideye/auth-react";
import { api, clearAuthState } from "@/services/api";

export interface UserInfo {
  user_id: string;
  email: string | null;
  name: string | null;
  company_id: string | null;
  roles: string[];
  is_admin: boolean;
  /** Access-group membership (resolved from Graph). Gates dashboard access. */
  has_access: boolean;
}

export interface AuthContextValue extends MideyeAuthContextValue<UserInfo> {
  /** Effective company for data queries (may differ from user.company_id for admins). */
  effectiveCompanyId: string | null;
  setEffectiveCompanyId: (id: string) => void;
  /** Companies available for admin company switching. Empty for non-admins. */
  availableCompanies: string[];
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthContextProvider({
  value,
  children,
}: {
  value: AuthContextValue;
  children: ReactNode;
}) {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

function CustomerAuthProviderContent({
  auth,
  children,
}: {
  auth: MideyeAuthContextValue<UserInfo>;
  children: ReactNode;
}) {
  const [effectiveCompanyId, setEffectiveCompanyId] = useState<string | null>(
    auth.user?.company_id ?? null,
  );
  const [availableCompanies, setAvailableCompanies] = useState<string[]>([]);
  const userId = auth.user?.user_id ?? null;
  const isAdmin = auth.user?.is_admin ?? false;

  useEffect(() => {
    if (!auth.isAuthenticated || !userId) {
      clearAuthState();
      return;
    }

    if (!isAdmin) return;

    let cancelled = false;
    api.auth.getCompanies()
      .then((data) => {
        if (!cancelled) setAvailableCompanies(data.companies ?? []);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, isAdmin, userId]);

  return (
    <AuthContextProvider
      value={{
        ...auth,
        effectiveCompanyId,
        setEffectiveCompanyId,
        availableCompanies: isAdmin ? availableCompanies : [],
      }}
    >
      {children}
    </AuthContextProvider>
  );
}


export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useMideyeAuth<UserInfo>();
  const companyStateKey = auth.user ? `${auth.user.user_id}:${auth.user.company_id ?? ""}` : "anonymous";

  return (
    <CustomerAuthProviderContent key={companyStateKey} auth={auth}>
      {children}
    </CustomerAuthProviderContent>
  );
}
